import cSvg from '#themes/default/default/components/svg/svg.js';

import template from './tooltip.html' with { type: 'html' };

export default {
	name: 'CTooltip',
	components: {
		cSvg
	},
	directives: {
		'click-away': {
			bind(element, binding, vnode) {
				let firstOpen = true;
				element.clickAway = (event) => {
					if (!firstOpen && !(element == event.target || element.contains(event.target))) {
						vnode.context[binding.expression](event);
					}
					firstOpen = false;
				};
				document.body.addEventListener('click', element.clickAway);
			},
			unbind(element) {
				document.body.removeEventListener('click', element.clickAway);
			}
		}
	},
	props: {
		align: {
			type: String,
			default: 'left'
		},
		direction: {
			type: String,
			required: true
		},
		forceShow: {
			type: Boolean,
			default: false
		},
		height: {
			type: String,
			default: 'auto'
		},
		isRtl: {
			type: Boolean,
			default: false
		},
		left: {
			type: String,
			default: ''
		},
		right: {
			type: String,
			default: ''
		},
		maxHeight: {
			type: String,
			default: 'none'
		},
		maxWidth: {
			type: String,
			default: 'none'
		},
		minHeight: {
			type: String,
			default: '0'
		},
		minWidth: {
			type: String,
			default: '0'
		},
		width: {
			type: String,
			default: '100%'
		},
		toggler: {
			type: String,
			required: true
		},
		trackingMethod: {
			type: Function,
			default: null
		},
		selectorString: {
			type: String,
			default: ''
		}
	},
	data() {
		return {
			show: false,
			doNotHide: false,
			srcElement: () => {},
			target: () => {}
		};
	},
	computed: {
		textDirection() {
			return this.isRtl ? 'right' : 'left';
		}
	},
	watch: {
		forceShow(value) {
			this.show = value;
		},
		show(value) {
			if (value) {
				this.$nextTick(() => {
					const tooltipContent = this.$refs.tooltipContent;
					const tooltip = this.closest(tooltipContent, (el) => el.classList.contains('tooltip'));
					const tooltipPointer = this.$refs.tooltipPointer;
					if (tooltipContent && tooltip && tooltipPointer) {
						this.setTooltip(tooltip, tooltipContent, this.direction, this.target, tooltipPointer);
					}
				});
			}
		},
		target(value) {
			const tooltipPointer = this.$refs.tooltipPointer;
			if (value && tooltipPointer) {
				this.$nextTick(() => this.setPointerPosition(this.target, tooltipPointer));
			}
		}
	},
	mounted() {
		const togglerElement = document.getElementById(this.toggler);
		if (togglerElement) {
			togglerElement.addEventListener('click', this.showTooltip);
		}
	},
	methods: {
		clickedAway(event) {
			const targetMatches = this.selectorString.length ? event.target.matches(this.selectorString) : false;
			const shouldHide = targetMatches ? !this.doNotHide : true;
			if (shouldHide) {
				this.show = false;
				this.doNotHide = false;
			}
		},
		closest(element, fn) {
			return element && (fn(element) ? element : this.closest(element.parentNode, fn));
		},
		setStyles(tooltipContent) {
			tooltipContent.style.width = this.width;
			tooltipContent.style.minWidth = this.minWidth;
			tooltipContent.style.maxWidth = this.maxWidth;
			tooltipContent.style.height = this.height;
			tooltipContent.style.minHeight = this.minHeight;
			tooltipContent.style.maxHeight = this.maxHeight;
			tooltipContent.style.textAlign = this.align;
		},
		setPosition(tooltip, tooltipContent, direction) {
			const tooltipOffset = {};
			tooltipOffset.top = {
				x: tooltip.offsetWidth / 2 - tooltipContent.offsetWidth / 2,
				y: -tooltipContent.offsetHeight - 10
			};
			tooltipOffset.right = {
				x: tooltip.offsetWidth + 10,
				y: -(tooltipContent.offsetHeight / 2 - tooltip.offsetHeight / 2)
			};
			tooltipOffset.bottom = {
				x: tooltip.offsetWidth / 2 - tooltipContent.offsetWidth / 2,
				y: tooltip.offsetHeight + 10
			};
			tooltipOffset.left = {
				x: -10 - tooltipContent.offsetWidth,
				y: -(tooltipContent.offsetHeight / 2 - tooltip.offsetHeight / 2)
			};
			const elementOffset = tooltipContent.getBoundingClientRect();
			const absTop = Math.abs(elementOffset.top);
			const absLeft = Math.abs(elementOffset.left);
			if (Math.abs(tooltipOffset[direction].x) > absLeft) {
				tooltipOffset[direction].x = direction === 'right' ? absLeft : -absLeft;
			}
			if (Math.abs(tooltipOffset[direction].y) > absTop) {
				tooltipOffset[direction].y = -absTop;
			}
			tooltipContent.style.top = `${tooltipOffset[direction].y}px`;
			if (this.right) {
				tooltipContent.style['right'] = this.right;
			} else {
				tooltipContent.style[this.textDirection] = this.left ? this.left : `${tooltipOffset[direction].x}px`;
			}
		},
		setPointerPosition(target, tooltipPointer) {
			if (!target) {
				return;
			}
			// Center a pointer to the target element
			const alignDirection = this.right ? 'right' : 'left';
			tooltipPointer.style[alignDirection] = `${target.offsetLeft + target.offsetWidth / 2 - 14}px`;
		},
		setTooltip(tooltip, tooltipContent, direction, target, tooltipPointer) {
			this.setStyles(tooltipContent);
			this.setPosition(tooltip, tooltipContent, direction);
			this.setPointerPosition(target, tooltipPointer);
		},
		showTooltip(event) {
			event.preventDefault();
			const targetMatches = this.selectorString.length ? event.target.matches(this.selectorString) : true;
			if (targetMatches) {
				this.show = true;
				this.target = event.target;
				this.target.setAttribute('tabindex', 0);
				// If other tooltip is visible, we need to wait until is closed. We can't use nextTick() here
				setTimeout(() => {
					const tooltipContent = document.querySelector('.tooltip_content');
					if (tooltipContent) {
						tooltipContent.setAttribute('tabindex', -1);
						tooltipContent.focus();
					}
				}, 0);
				// Call tracking method if it was provided
				if (this.trackingMethod && typeof this.trackingMethod === 'function') {
					this.trackingMethod();
				}
				// Do not hide tooltip by clicking on the other toggler in product note
				this.doNotHide = this.srcElement !== event.srcElement;
				this.srcElement = event.srcElement;
			} else {
				this.show = event.currentTarget && this.show ? event.currentTarget.classList.contains('tooltip') : false;
			}
		}
	},
	template
};
